import { useEffect, useState } from "react";

/**
 * Circular countdown timer — a ring that "discharges" from full to empty
 * as the seconds tick down, with the remaining seconds in the center.
 *
 * Props:
 *   startedAt : ISO string OR Date — moment the window opened
 *   totalSec  : total window length (default 90)
 *   size      : outer size in px (default 96)
 *   stroke    : ring thickness (default 8)
 *   label     : small caption below the number (e.g. "Waiting for driver")
 *   onExpire  : optional callback fired once when the timer hits 0
 *
 * The ring colour ramps from teal → amber → red as the window empties so
 * the rider feels urgency without needing to read the number.
 */
export default function CircularCountdown({
  startedAt,
  totalSec = 90,
  size = 96,
  stroke = 8,
  label,
  onExpire,
  className = "",
  testId,
}) {
  const [now, setNow] = useState(() => Date.now());
  const [fired, setFired] = useState(false);
  // Tracks whether we have already pulsed the device for THIS run of the
  // countdown. Reset when `startedAt` changes (a fresh counter) so the
  // user gets a nudge each new round.
  const [nudged, setNudged] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { setFired(false); setNudged(false); }, [startedAt]);

  const startMs = (() => {
    if (!startedAt) return now;
    try {
      const d = startedAt instanceof Date ? startedAt : new Date(startedAt);
      const t = d.getTime();
      return Number.isFinite(t) ? t : now;
    } catch (e) { return now; }
  })();

  const elapsed = Math.max(0, (now - startMs) / 1000);
  const remaining = Math.max(0, totalSec - elapsed);
  const pct = Math.max(0, Math.min(1, remaining / totalSec));

  useEffect(() => {
    if (remaining <= 0 && !fired) {
      setFired(true);
      if (typeof onExpire === "function") onExpire();
    }
  }, [remaining, fired, onExpire]);

  // Soft haptic nudge once per run — fires the first time the ring
  // enters the "last 10 seconds" zone so the user feels the clock
  // ticking without staring at the screen. No-op on devices without
  // the Vibration API (iOS Safari, desktop); the visual flash handles
  // those. Respects the user's `yorm_haptics_enabled` preference from
  // Settings → Privacy so system-level haptic choices are honoured.
  useEffect(() => {
    if (nudged || remaining <= 0 || remaining > 10) return;
    setNudged(true);
    let enabled = true;
    try {
      const v = localStorage.getItem("yorm_haptics_enabled");
      if (v !== null) enabled = v === "true";
    } catch { /* ignore */ }
    if (!enabled) return;
    try { navigator.vibrate && navigator.vibrate([35, 60, 35]); } catch { /* ignore */ }
  }, [remaining, nudged]);

  // Flash state — the whole chip pulses in the last 10 s so even users
  // with vibration disabled catch the urgency.
  const flash = remaining > 0 && remaining <= 10;

  // Colour ramp: >50% teal, 25-50% amber, <25% red.
  const ringColor = pct > 0.5 ? "#4F988E" : pct > 0.25 ? "#f59e0b" : "#ef4444";
  const trackColor = "#E5E7EB";

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct);
  const seconds = Math.ceil(remaining);

  return (
    <div
      className={`inline-flex flex-col items-center ${flash ? "animate-pulse" : ""} ${className}`}
      data-testid={testId || "circular-countdown"}
      data-flash={flash ? "1" : "0"}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={trackColor} strokeWidth={stroke}
          />
          <circle
            cx={size / 2} cy={size / 2} r={radius}
            fill="none" stroke={ringColor} strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 250ms linear, stroke 400ms ease" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="font-semibold tabular-nums leading-none"
            style={{ color: ringColor, fontSize: Math.max(11, Math.round(size * 0.3)) }}
            data-testid="circular-countdown-value"
          >
            {seconds}
          </span>
        </div>
      </div>
      {label && (
        <p className="text-xs text-gray-600 mt-2 text-center max-w-[180px]">{label}</p>
      )}
    </div>
  );
}
